import moment from 'moment';
import AppButton from 'src/components/base/Button/Button';
import { TimeZoneAbbreviated } from 'src/latest/src';
import Avatar from 'src/components/base/Avatar/Avatar';
import CustomTooltip from 'src/components/base/Tooltip/Tooltip';
import location from 'src/assets/icons/icon-inPersonSession.svg';
import {
  extractContentFromHTML,
  stringToObject,
} from 'src/legacy/utils/helpers';
import { useIsMobile } from 'src/latest/src/hooks';
import { MeetingInformation } from '../../types/MeetingInformation';
import './MeetingInfoCard.scss';
import { Typography } from 'src/latest/src/components';

type Props = {
  meetingInformation: MeetingInformation;
  onActionButtonClick?: (meetingStatus: string | undefined) => void;
  actionButtonLabel?: string;
  isFromProviderDashboard?: boolean;
};

const MeetingInfoCard = ({
  meetingInformation,
  onActionButtonClick,
  actionButtonLabel,
  isFromProviderDashboard,
}: Props) => {
  const isFromMobile = useIsMobile();

  function buttonAction() {
    const metadata: any = stringToObject(meetingInformation?.office_address);

    const address = {
      addressInfo1: metadata?.addressInfo1,
      addressInfo2: metadata?.addressInfo2,
      addressInfo3: metadata?.addressInfo3,
    };

    const addressString = extractContentFromHTML(
      `${address.addressInfo1} ${address.addressInfo2} ${address.addressInfo3}`,
    );

    window.open(
      metadata.mapUrl ||
        `https://www.google.com/maps/search/?api=1&query=${addressString}`,
      `${isFromMobile ? '_top' : '_blank'}`,
    );
  }

  return (
    <div
      className={`meeting-card ${
        isFromProviderDashboard ? 'provider-card' : 'default-card'
      } text-center cursor-pointer`}
    >
      {!isFromProviderDashboard && (
        <div className="meeting-card__image">
          <Avatar
            className="meeting-card__avatar mx-auto"
            size="122px"
            photoUrl={meetingInformation?.therapist_profile_pic_url}
            fullName={meetingInformation?.therapist_name}
          />
        </div>
      )}

      <div className="meeting-card__content">
        <Typography
          className={`meeting-card__title ${
            isFromProviderDashboard ? 'provider-service-type' : ''
          }`}
          variant="size-18"
          color="color-2"
          weight="normal"
        >
          {meetingInformation?.service_type}
        </Typography>

        {!isFromProviderDashboard && (
          <Typography className="text-center me-2 mt-4">
            <div className="meeting-card__with">
              <Typography className="me-1">with</Typography>

              <CustomTooltip
                text={meetingInformation?.therapist_name}
                placement="bottom"
                force={false}
              >
                <Typography weight="bold">
                  <i>
                    {meetingInformation?.therapist_name.length > 21
                      ? `${meetingInformation?.therapist_name.substring(
                          0,
                          18,
                        )}...`
                      : meetingInformation?.therapist_name}
                  </i>
                </Typography>
              </CustomTooltip>

              <Typography className="ms-1">
                {meetingInformation?.therapist_pronoun}
              </Typography>
            </div>
          </Typography>
        )}

        <Typography
          color="color-2"
          weight="regular"
          variant="size-18"
          className="text-center mt-4 booking-welcome-greetings__contents-wrapper__text ms-0"
        >
          <div
            className={`DateTime-onText d-flex justify-content-center ${
              isFromProviderDashboard ? 'provider-date-time' : ''
            }`}
          >
            <Typography
              className={` me-1 ${
                isFromProviderDashboard ? 'provider-default-text' : ''
              }`}
            >
              on
            </Typography>

            <Typography className="DateTime-Text ms-0 Adjust-CST">
              <span
                className={`${
                  isFromProviderDashboard ? 'provider-time-value' : ''
                }`}
              >
                {moment(meetingInformation?.meeting_time).format(
                  'dddd, MMMM D • h:mm A',
                )}
              </span>

              <span
                className={` ${
                  isFromProviderDashboard ? 'provider-default-text' : ''
                }`}
              >
                {` (${TimeZoneAbbreviated(meetingInformation?.meeting_time)})`}
              </span>
            </Typography>
          </div>
        </Typography>

        <Typography className="text-center mt-4 mb-4">
          <div className="meeting-card__with">
            <Typography
              className={`me-2 mb-3 ${
                isFromProviderDashboard ? 'provider-session-text' : ''
              }`}
              weight="normal"
            >
              <i>
                <b>
                  {meetingInformation?.meeting_type === 'INTAKE_IN_PERSON'
                    ? 'In-Person'
                    : 'Online'}{' '}
                  Session
                </b>
              </i>
            </Typography>

            {meetingInformation?.meeting_type.includes('PERSON') && (
              <img
                className="meeting-card__location-icon"
                src={location}
                alt="location"
                onClick={buttonAction}
              />
            )}
          </div>
        </Typography>
      </div>

      {!isFromProviderDashboard && onActionButtonClick && (
        <AppButton
          variant="primary"
          textSize="size-14"
          textWeight="bold"
          label={actionButtonLabel}
          onClick={() =>
            onActionButtonClick(meetingInformation?.meeting_status)
          }
          className="meeting-card__button px-5 py-3 d-block mx-auto"
        />
      )}
    </div>
  );
};

export default MeetingInfoCard;
